import { A11yModule } from '@angular/cdk/a11y';
import {
  GlobalPositionStrategy,
  Overlay,
  OverlayRef,
} from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  DOCUMENT,
  effect,
  inject,
  input,
  linkedSignal,
  OnDestroy,
  OnInit,
  output,
  signal,
  TemplateRef,
  untracked,
  viewChild,
  ViewContainerRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterModule } from '@angular/router';
import { hasActiveDescendant as hasActiveDescendantUtil } from 'app/core/customer-menus/menu-active.util';
import { getMenu } from 'app/core/customer-menus/menu-utilities';
import { MenuItem } from 'app/core/models/menu/menu.model';
import { ROUTE_IDENTIFIER_NAMES } from 'app/core/models/route-identifiers/route-identifiers.model';
import { SecurityRole } from 'app/core/models/security/security-role';
import { AuthService } from 'app/core/services/auth/auth.service';
import { ConfigService } from 'app/core/services/config/config.service';
import { MenuCountsService } from 'app/core/services/menu/menu-counts.service';
import { NavActiveUrlService } from 'app/core/services/menu/nav-active-url.service';
import { combineLatest } from 'rxjs';
import { ButtonComponent } from '../components/button/button.component';
import { MessageComponent } from '../components/message/message.component';
import { MenuItemComponent } from '../menu-item/menu-item.component';

// The collapsed rail is 75px wide (matches app.component.ts's `sidenavWidth`),
// so the flyout sits flush against its right edge. Vertically it aligns with
// the rail icon that opened it; the app header is 67px tall, which is where the
// rail itself starts and so the highest the panel can go.
const FLYOUT_LEFT_OFFSET = '75px';
const FLYOUT_MIN_TOP_PX = 67;
// The panel gives up its alignment with the trigger rather than shrink past
// this: its header row and each submenu row are 40px, so this keeps the header
// plus two rows on screen. A panel allowed to collapse further would still
// capture focus while being effectively invisible.
//
// Lowered from 160 (header + three rows) to 120 for #6559. Alignment is what
// the ticket asks for and this constant is the only thing that overrides it, so
// the lower it goes the further down the rail an icon can sit and still open
// level with its flyout — worth ~40px more page scroll for the lowest icon.
// Two rows is the floor for that trade: a header plus a single row reads as a
// rendering fault rather than a short menu.
const FLYOUT_MIN_HEIGHT_PX = 120;

@Component({
  selector: 'sqep-custom-sidenav',
  imports: [
    A11yModule,
    MatListModule,
    MatSidenavModule,
    RouterModule,
    MatIconModule,
    MatTooltipModule,
    MenuItemComponent,
    MessageComponent,
    ButtonComponent,
  ],
  templateUrl: './custom-sidenav.component.html',
  styleUrl: './custom-sidenav.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CustomSidenavComponent implements OnInit, OnDestroy {
  readonly clientLogoAsset2 = input<string>();
  readonly heading = input<string>();
  readonly subTitle = input<string>();
  readonly applicationName = input<string>();
  readonly collapsed = input<boolean>(false);
  readonly isViewingOther = input<boolean>(false);
  readonly viewingPersonName = input<string | null>(null);

  private readonly authService = inject(AuthService);
  private readonly document = inject(DOCUMENT);
  private readonly overlay = inject(Overlay);
  private readonly viewContainerRef = inject(ViewContainerRef);
  private readonly navActiveUrl = inject(NavActiveUrlService);
  protected readonly menuCounts = inject(MenuCountsService).counts;
  private readonly configService = inject(ConfigService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly flyoutTemplate =
    viewChild<TemplateRef<unknown>>('flyoutContent');
  private securityRoleLookup: { [role_identifier: string]: SecurityRole };
  private userSecurityLevel: number;
  private isManager: boolean;
  private isAssessor: boolean;
  private isTrainingProviderAdmin: boolean;
  private isSuperuser: boolean;
  private isOrgUnitRestricted: boolean;
  private overlayRef: OverlayRef | null = null;
  private flyoutPosition: GlobalPositionStrategy | null = null;
  private lastFlyoutTrigger: HTMLElement | null = null;
  private pendingFlyoutFrame: number | null = null;
  // The top last written to the position strategy. A plain field, not a read of
  // flyoutTop(): measurement runs inside the effect that attaches the overlay,
  // so reading the signal there would make that effect depend on a signal it
  // also writes, tearing the panel down and rebuilding it on every measurement.
  private appliedFlyoutTop: number | null = null;
  // Coalesced into a frame: the capture-phase listener fires for every scroller
  // in the document, not just the page, and each measurement reads layout and
  // then writes the overlay's position — so left as-is, scrolling a long table
  // under an open flyout forces a reflow on every tick.
  private readonly remeasureFlyout = (event?: Event) => {
    // The panel scrolling inside itself cannot move its own bounds, and the
    // capture-phase listener sees those events too. Re-measuring on them made
    // the panel resize under the pointer while the user was dragging its
    // scrollbar.
    // instanceof Node, not a plain truthy check: a scroll dispatched on window
    // reports window as its target, which contains() rejects outright.
    const target = event?.target;
    if (
      target instanceof Node &&
      this.overlayRef?.overlayElement.contains(target)
    ) {
      return;
    }
    if (this.pendingFlyoutFrame !== null) {
      return;
    }
    this.pendingFlyoutFrame = requestAnimationFrame(() => {
      this.pendingFlyoutFrame = null;
      this.measureFlyoutBounds();
    });
  };

  protected readonly menuData = signal<MenuItem[]>([]);
  protected readonly profilePicSize = computed(() =>
    this.collapsed() ? '32' : '64',
  );
  protected changeCollapsedState = output<boolean>();
  protected clearViewingAs = output<void>();
  protected readonly openItem = signal<MenuItem | null>(null);
  protected readonly openTopItem = linkedSignal<MenuItem | null>(
    () =>
      this.menuData().find((item) =>
        hasActiveDescendantUtil(item, this.currentUrl()),
      ) ?? null,
  );
  protected readonly openFlyoutChild = signal<MenuItem | null>(null);
  protected readonly flyoutMaxHeight = signal(0);
  protected readonly flyoutTop = signal(FLYOUT_MIN_TOP_PX);

  private readonly currentUrl = this.navActiveUrl.currentUrl;

  constructor() {
    // Depends on openItem() alone. The template is read untracked because
    // attaching the portal creates an embedded view in this component's
    // ViewContainerRef, which dirties the view queries this effect would
    // otherwise subscribe to — so attaching re-triggered the effect, whose
    // cleanup detached and rebuilt the panel underneath the user. Mid-scroll
    // that discarded the scroll position and drew a fresh scrollbar (#6262).
    // `flyoutContent` is a static top-level ng-template, resolved during the
    // first change detection and long before any item can be opened, so
    // nothing is lost by not reacting to it. Teardown is ngOnDestroy's
    // overlayRef.dispose(), not a per-run cleanup.
    effect(() => {
      const item = this.openItem();
      const template = untracked(this.flyoutTemplate);
      if (item && template) {
        this.attachFlyout(template);
      } else {
        this.detachFlyout();
      }
    });

    effect(() => {
      const item = this.openItem();
      this.openFlyoutChild.set(null);
      if (!item) {
        return;
      }
      const url = this.currentUrl();
      const activeChild = item.subMenu?.find((child) =>
        hasActiveDescendantUtil(child, url),
      );
      if (activeChild) {
        this.openFlyoutChild.set(activeChild);
      }
    });
  }

  ngOnInit(): void {
    combineLatest([
      this.authService.securityRoleLookupSubj,
      this.authService.loggedInUserSubj,
    ])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ([securityRole, loggedInUser]) => {
          this.securityRoleLookup = securityRole;
          this.userSecurityLevel = loggedInUser.securityLevel;
          this.isManager = loggedInUser.managerTypeId > 0;
          this.isAssessor = loggedInUser.isAssessor;
          this.isTrainingProviderAdmin =
            this.authService.isTrainingProviderAdmin;
          this.isSuperuser = this.authService.isSuperuser;
          this.isOrgUnitRestricted = this.authService.isOrgUnitRestricted;
          this.menuData.set(
            this.filterMenu([...getMenu(this.configService.clientAccountMenu)]),
          );
        },
      });
  }

  ngOnDestroy(): void {
    this.stopTrackingFlyout();
    this.overlayRef?.dispose();
  }

  protected hasActiveDescendant(item: MenuItem): boolean {
    return hasActiveDescendantUtil(item, this.currentUrl());
  }

  protected toggleFlyout(
    item: MenuItem,
    trigger: HTMLElement | null = null,
  ): void {
    const opening = this.openItem() !== item;
    // Captured here rather than derived when the overlay attaches: attaching
    // happens in an effect, and by the time that runs cdkTrapFocusAutoCapture
    // may already have moved document.activeElement into the panel.
    if (opening) {
      this.lastFlyoutTrigger =
        trigger ?? (document.activeElement as HTMLElement);
    }
    this.openItem.set(opening ? item : null);
  }

  protected closeFlyout(): void {
    this.openItem.set(null);
  }

  protected toggleTopItem(item: MenuItem): void {
    this.openTopItem.set(this.openTopItem() === item ? null : item);
  }

  protected toggleFlyoutChild(child: MenuItem): void {
    this.openFlyoutChild.set(this.openFlyoutChild() === child ? null : child);
  }

  protected onFlyoutNavigated(): void {
    this.closeFlyout();
    this.changeCollapsedState.emit(true);
  }

  private attachFlyout(template: TemplateRef<unknown>): void {
    if (!this.overlayRef) {
      this.flyoutPosition = this.overlay
        .position()
        .global()
        .left(FLYOUT_LEFT_OFFSET)
        .top(`${FLYOUT_MIN_TOP_PX}px`);
      this.overlayRef = this.overlay.create({
        positionStrategy: this.flyoutPosition,
        hasBackdrop: false,
        panelClass: 'menu-flyout-overlay-panel',
      });
      // No backdrop: a real backdrop element would cover the collapsed rail
      // itself (global position strategy sizes it to the full viewport), so
      // clicking a different rail icon would hit the backdrop and require a
      // second click to actually open that item's flyout. outsidePointerEvents
      // detects the same click without an intervening element, so the rail
      // icon's own (click) handler still fires underneath it. Clicks on the
      // rail are left alone here — toggleFlyout() already closes the open
      // item when its own icon is clicked again and switches when a different
      // icon is clicked, so closing here too would cause it to immediately
      // reopen instead of toggling shut.
      this.overlayRef.outsidePointerEvents().subscribe((event) => {
        const target = event.target as HTMLElement | null;
        if (target?.closest('.menu-rail')) {
          return;
        }
        this.closeFlyout();
      });
      this.overlayRef.keydownEvents().subscribe((event) => {
        if (event.key === 'Escape') {
          this.closeFlyout();
        }
      });
    }
    this.startTrackingFlyout();
    if (!this.overlayRef.hasAttached()) {
      this.overlayRef.attach(
        new TemplatePortal(template, this.viewContainerRef),
      );
    }
    // Measures the trigger and the footer, not the panel, so it does not matter
    // whether the rows have rendered yet — and running here, before the first
    // paint, is what stops the panel appearing at the previous position and
    // then jumping.
    this.measureFlyoutBounds();
  }

  // Aligns the panel's top edge with the rail icon that opened it, so its
  // header row sits alongside that icon rather than always at the top of the
  // rail. The rail is fixed in the viewport but scrolls internally once the
  // menu is long enough, so the trigger is re-read on every measurement.
  //
  // The height is capped rather than fixed: the panel sizes to its own rows,
  // but the footer paints over the fixed sidenav because it comes later in the
  // document at the same stacking level, whereas the flyout lives in the CDK
  // overlay container (z-index 1100) where nothing covers it. A submenu long
  // enough to reach the bottom of the page would still paint over the footer
  // (#6262), so cap it at the space left above the footer and scroll inside.
  //
  // Alignment with the trigger is the rule, not a preference (#6559). #6262
  // originally treated it as negotiable and let the panel grow upwards when its
  // rows would not fit below the icon, so that a short screen used the empty
  // band above the trigger instead of scrolling. That was rejected: a panel
  // that opens somewhere other than beside the icon that opened it reads as a
  // glitch, and it moved again whenever the rows changed. The panel now stays
  // put and scrolls.
  //
  // Dropping that also removed the panel's own height from this calculation
  // entirely, which is what actually fixed #6559: the height could only be read
  // from the rendered rows, and every caller ran before Angular had rendered
  // them, so the position was derived from whatever the panel held a moment
  // earlier. Switching flyouts placed the new panel using the old menu's rows;
  // collapsing a submenu placed it using the expanded height. Nothing here
  // measures the panel any more, so there is no longer a render to race.
  //
  // FLYOUT_MIN_HEIGHT_PX is the one thing that can still override alignment: an
  // icon sitting within that of the footer (or below it) would otherwise get a
  // panel too short to use, and a focus-trapping `role="dialog"` with nothing
  // visible in it is worse than shifting it up those last few pixels.
  private measureFlyoutBounds(): void {
    // Defensive — only one footer renders at a time today (the passport's own
    // sits in its not-logged-in branch and is suppressed by the footer's
    // `@if (isSignedIn())`). Tops at or above the panel's ceiling are dropped
    // because they can't bound it: a `display: none` footer measures all
    // zeros and one scrolled above the viewport measures negative, either of
    // which would win the `min` and collapse the panel to nothing.
    const footerTops = Array.from(
      this.document.querySelectorAll('sqep-footer footer'),
    )
      .map((footer) => footer.getBoundingClientRect().top)
      .filter((footerTop) => footerTop > FLYOUT_MIN_TOP_PX);
    const flyoutBottom = Math.min(window.innerHeight, ...footerTops);

    const triggerTop =
      this.lastFlyoutTrigger?.getBoundingClientRect().top ?? FLYOUT_MIN_TOP_PX;
    // The highest the top may sit and still leave a usable panel. Goes negative
    // on a band shorter than the minimum, which the floor below then wins.
    const lowestUsableTop = flyoutBottom - FLYOUT_MIN_HEIGHT_PX;
    const top = Math.max(
      FLYOUT_MIN_TOP_PX,
      Math.min(triggerTop, lowestUsableTop),
    );

    this.flyoutTop.set(top);
    this.flyoutMaxHeight.set(Math.max(0, flyoutBottom - top));
    // The rail is fixed in the viewport, so scrolling the page rarely moves the
    // trigger: skip the overlay write unless the top actually changed.
    if (top !== this.appliedFlyoutTop) {
      this.appliedFlyoutTop = top;
      this.flyoutPosition?.top(`${top}px`);
      if (this.overlayRef?.hasAttached()) {
        this.overlayRef.updatePosition();
      }
    }
  }

  private startTrackingFlyout(): void {
    // Capture phase so scrolling inside a nested scroller is picked up too —
    // the rail has its own. Re-adding the same handler reference is a no-op,
    // so switching directly between flyouts can't stack duplicate listeners.
    window.addEventListener('scroll', this.remeasureFlyout, {
      passive: true,
      capture: true,
    });
    window.addEventListener('resize', this.remeasureFlyout, { passive: true });
  }

  private stopTrackingFlyout(): void {
    window.removeEventListener('scroll', this.remeasureFlyout, {
      capture: true,
    });
    window.removeEventListener('resize', this.remeasureFlyout);
    // A frame already queued would otherwise measure against a detached panel.
    if (this.pendingFlyoutFrame !== null) {
      cancelAnimationFrame(this.pendingFlyoutFrame);
      this.pendingFlyoutFrame = null;
    }
  }

  private detachFlyout(): void {
    this.stopTrackingFlyout();
    if (this.overlayRef?.hasAttached()) {
      const focusStillInFlyout = this.overlayRef.overlayElement.contains(
        document.activeElement,
      );
      this.overlayRef.detach();
      if (focusStillInFlyout) {
        this.lastFlyoutTrigger?.focus();
      }
      // The trigger is deliberately kept: this also runs as the attaching
      // effect's cleanup, immediately before it re-attaches, and clearing it
      // there would lose the icon the flyout was opened from. toggleFlyout()
      // replaces it on the next open.
    }
  }

  private filterMenu(items: MenuItem[]): MenuItem[] {
    return items
      .filter((item) => this.showMenuItem(item))
      .map((item) => ({
        ...item,
        subMenu: item.subMenu ? this.filterMenu(item.subMenu) : undefined,
      }))
      .filter(
        (item) => !!item.route || !item.subMenu || item.subMenu.length > 0,
      );
  }

  private showMenuItem(menuItem: MenuItem): boolean {
    if (menuItem.hideItem) {
      return false;
    }

    if (menuItem.hideWhenOrgUnitRestricted && this.isOrgUnitRestricted) {
      return false;
    }

    const isTrainingProvidersItem =
      menuItem.routeIdentifier ===
        ROUTE_IDENTIFIER_NAMES.TRAINING_MANAGEMENT_PROVIDERS ||
      menuItem.routeIdentifier === ROUTE_IDENTIFIER_NAMES.TRAINING_MANAGEMENT;

    // TP admins and superusers always see the Training Management parent and child
    // regardless of securityRoles configured for that item, but a tenant that
    // hides the item entirely (hideItem: true) still hides it for everyone.
    if (
      (this.isTrainingProviderAdmin || this.isSuperuser) &&
      isTrainingProvidersItem
    ) {
      return true;
    }

    const hasManagementAccess =
      menuItem.allowManagementPositionAccess && this.isManager;

    let hasRequiredSecurityLevel: boolean;
    if (Array.isArray(menuItem.securityRoles)) {
      hasRequiredSecurityLevel = menuItem.securityRoles.some(
        (role) =>
          this.securityRoleLookup[role]?.securityLevel ===
          this.userSecurityLevel,
      );
    } else {
      hasRequiredSecurityLevel =
        this.securityRoleLookup[menuItem.securityRoles]?.securityLevel <=
        this.userSecurityLevel;
    }

    const visible =
      menuItem.assessorAccess === 'only'
        ? hasRequiredSecurityLevel && this.isAssessor
        : menuItem.assessorAccess === 'hide'
          ? (hasRequiredSecurityLevel || hasManagementAccess) &&
            !this.isAssessor
          : hasRequiredSecurityLevel || hasManagementAccess;

    if (!visible || !this.isTrainingProviderAdmin || this.isSuperuser) {
      return visible;
    }

    // TP admin: visible passed the security level check (same level as Admin).
    // Block Admin-exclusive items — array-role items use exact-level matching
    // so they are Admin-only by design.
    if (Array.isArray(menuItem.securityRoles)) {
      return false;
    }

    // For range-based checks (>=), only show items accessible to a plain User.
    const minRequiredLevel =
      this.securityRoleLookup[menuItem.securityRoles]?.securityLevel ?? 0;
    const userLevel =
      this.securityRoleLookup['SECURITY_ROLE_USER']?.securityLevel ?? 0;
    return minRequiredLevel <= userLevel;
  }
}
